import { FC, Fragment, useEffect, useState } from 'react';
import { Link, matchPath, useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';

import { ActionMenu, Avatar, Eyebrow, Icon, Tag } from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';

import { environment, SiteMode } from '../../api';
import { AUTH_STORE_TOKEN } from '../../auth';
import { topics } from '../../docs/assets/topics';
import { isLocalPath, Paths } from '../../paths';
import logoUrl from '../assets/logo.svg';
import * as Styles from './header.module.scss';

const docsMenuLinks = [
    ...topics.map(topic => ({
        path: `/docs/${topic.id}/${topic.subtopics[0].id}`,
        text: topic.title,
    })),
    { path: '/docs/apis', text: 'Reference' },
    ...(environment.site.mode === SiteMode.TenantApis
        ? [{ path: '/docs/release-notes', text: 'Release Notes' }]
        : []),
];

export const Header: FC = observer(() => {
    const [authStore] = useDependencies(AUTH_STORE_TOKEN);
    const [docsMenuOpen, setDocsMenuOpen] = useState(false);
    const [accountMenuOpen, setAccountMenuOpen] = useState(false);
    const history = useHistory();
    const [pathName, setPathName] = useState(history.location.pathname); // needed for MobX

    useEffect(() => {
        return history.listen(location => {
            setPathName(location.pathname);
        });
    }, [history]);

    const createNavLink = (path: string, text: string) =>
        isLocalPath(path) ? (
            <Link className={Styles.navLink} to={path} aria-current={!!matchPath(pathName, path)}>
                <span>{text}</span>
            </Link>
        ) : (
            <a className={Styles.navLink} href={path} target="_blank" rel="noopener noreferrer">
                {text}
            </a>
        );

    return (
        <header className={Styles.header}>
            <Link className={Styles.logo} to="/">
                <Avatar image={logoUrl} title="ServiceTitan" />
                <span>{environment.site.title}</span>
            </Link>
            <span className={Styles.divider} />
            <ActionMenu
                trigger={
                    <a
                        className={Styles.navLink}
                        href="#"
                        aria-current={!!matchPath(pathName, '/docs')}
                        aria-expanded={docsMenuOpen}
                        onClick={event => {
                            setDocsMenuOpen(!docsMenuOpen);
                            event.preventDefault();
                        }}
                    >
                        <span>Docs</span>
                        <Icon
                            className="m-l-1"
                            name={docsMenuOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                        />
                    </a>
                }
                offset={{ distance: 20 }}
                sharp
                open={docsMenuOpen}
                onClickOutside={() => {
                    setDocsMenuOpen(false);
                }}
            >
                {docsMenuLinks.map(link => (
                    <ActionMenu.Item
                        key={link.path}
                        onClick={() => {
                            setDocsMenuOpen(false);
                            history.push(link.path);
                        }}
                    >
                        {link.text}
                    </ActionMenu.Item>
                ))}
            </ActionMenu>
            {authStore.isAuthenticated && createNavLink('/apps', 'My Apps')}
            {environment.site.mode === SiteMode.TenantApis &&
                createNavLink(Paths.customerIntegrationPaths, 'Customer Integration Paths')}
            <span className="k-spacer" />
            {environment.site.environmentName && (
                <Tag color="warning">{environment.site.environmentName}</Tag>
            )}
            {!authStore.isAuthenticated ? (
                <Fragment>
                    {createNavLink(Paths.requestAccess, 'Request Access')}
                    {createNavLink('/signin', 'Sign In')}
                </Fragment>
            ) : (
                <ActionMenu
                    trigger={
                        <Icon
                            name="account_circle"
                            size={24}
                            onClick={() => {
                                setAccountMenuOpen(!accountMenuOpen);
                            }}
                        />
                    }
                    direction="bl"
                    offset={{ distance: 20 }}
                    sharp
                    open={accountMenuOpen}
                    onClickOutside={() => {
                        setAccountMenuOpen(false);
                    }}
                >
                    <ActionMenu.Item disabled>
                        <Eyebrow>{authStore.userName}</Eyebrow>
                    </ActionMenu.Item>
                    <ActionMenu.Item
                        onClick={() => {
                            authStore.signOut();
                        }}
                    >
                        Sign Out
                    </ActionMenu.Item>
                </ActionMenu>
            )}
        </header>
    );
});
Header.displayName = 'Header';
